import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_academicPeriodMutation,
  useGET_academicPeriodByIdQuery,
  usePUT_academicPeriodMutation,
  usePOST_academicPeriodMutation,
} from 'redux/api/academicPeriodsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/academicPeriodModel'
import { crudColorMap, crudIconMap, genderSelectData } from 'utils/helpers'

import { ActionType, IAcademicPeriod } from 'types'
import useCrudData from 'utils/customHooks/useCrudData'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const AcademicPeriodForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/academic-periods'
  const editSlug = `/academic-periods/edit/${id}`

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('school.academic-periods')}`,
    entityId: id,
    getEntityQuery: useGET_academicPeriodByIdQuery,
    createEntityMutation: usePOST_academicPeriodMutation,
    editEntityMutation: usePUT_academicPeriodMutation,
    deleteEntityMutation: useDELETE_academicPeriodMutation
  })

  const {
    title,
    academic_year,
    start_date,
    end_date,
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity,
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        const data: IAcademicPeriod = {
          ...values,
        }

        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputField fieldProps={{ ...academic_year }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePickerField fieldProps={{ ...start_date }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePickerField fieldProps={{ ...end_date }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default AcademicPeriodForm
